import { ButtonProps, IconProps } from '@chakra-ui/react';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSnackbar } from 'ui-components';
import Tooltip from '~/components/Tooltip';
import {
  PDF_DOCUMENT_TYPE,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { isInstagramPlatformConnected } from '~/creatorUtils';
import { captureException } from '~/errorsLogging';
import { pdfApi } from '~/pdfApi';
import { formatToolTipMessage } from '~/screens/MediaPackDetailScreen/components/MediaPackExportPdfDesktopButton';

import { ExportPdfButton } from './ExportPdfButton';

interface Props extends ButtonProps {
  data: any;
  fileName?: string;
  iconProps?: IconProps;
  isSharedView?: boolean;
}

export const ExportPdfButtonContainer: React.FC<Props> = ({
  isSharedView = false,
  fileName = 'media-pack.pdf',
  data = {} as any,
  iconProps = {} as IconProps,
  ...props
}) => {
  const snackbar = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const { creator, creatorBenchmarks, creatorInsights, mediaPack, pdfImages } =
    data;
  const requiredPdfData =
    creator && creatorBenchmarks && creatorInsights && mediaPack && pdfImages;

  const hasInstagram = data.mediaPack.platforms?.includes?.(
    SOCIAL_PLATFORMS.instagram,
  );
  const isInstagramConnected = isInstagramPlatformConnected(creator);
  const canDownloadPdf =
    hasInstagram && requiredPdfData && isInstagramConnected;

  const handleDownloadPdf = async () => {
    setIsLoading(true);

    try {
      const pdfStream = await pdfApi.create({
        pdfType: PDF_DOCUMENT_TYPE.MEDIA_PACK,
        data: data,
      });
      const pdfStreamData = pdfStream.data;

      saveAs(pdfStreamData, fileName);
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error, {
        tags: { section: 'pdf-generation' },
        errorInfo: { pdfData: data },
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!canDownloadPdf && isSharedView) {
    return null;
  }

  if (!canDownloadPdf) {
    return (
      <Tooltip
        backgroundColor="white"
        isTouchFriendly
        label={
          formatToolTipMessage(isInstagramConnected, hasInstagram) ||
          !requiredPdfData
        }
        rounded="md"
        shouldWrapChildren
      >
        <ExportPdfButton
          marginRight={2}
          iconProps={iconProps}
          isDisabled
          {...props}
        />
      </Tooltip>
    );
  }

  return (
    <ExportPdfButton
      onClick={handleDownloadPdf}
      isLoading={isLoading}
      marginRight={2}
      iconProps={iconProps}
      {...props}
    />
  );
};
